<script setup lang="ts">
import type { WalletTabKey } from '~/features/my-wallet/constants/wallet-tabs'
import { SolTabs } from '@solfacil/girassol'
import { useI18n } from 'vue-i18n'
import ReceivingModelTab from '~/features/my-wallet/blocks/receiving-model/ReceivingModelTab.vue'
import StatusBanner from '~/features/my-wallet/blocks/status-banner/StatusBanner.vue'
import SummaryTab from '~/features/my-wallet/blocks/summary/SummaryTab.vue'
import { useMyWalletAccess } from '~/features/my-wallet/composables/useMyWalletAccess'
import {
  DEFAULT_WALLET_TAB,
  isWalletTab,
  RECEIVING_MODEL_TAB,
  resolveLegacyContext,
  WALLET_TABS,
} from '~/features/my-wallet/constants/wallet-tabs'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const { isEnabled: isMyWalletEnabled, isLoading: isLoadingFlag } = useMyWalletAccess()

const activeTab = computed<WalletTabKey>(() => {
  const tabFromQuery = route.query.tab
  return isWalletTab(tabFromQuery) ? tabFromQuery : DEFAULT_WALLET_TAB
})

watch([isMyWalletEnabled, isLoadingFlag, () => route.query], ([enabled, loading, query]) => {
  if (loading)
    return

  if (enabled !== true) {
    router.replace('/')
    return
  }

  const { ctx, ...queryWithoutContext } = query
  if (ctx !== undefined) {
    router.replace({ path: route.path, query: { ...queryWithoutContext, tab: resolveLegacyContext(ctx) } })
    return
  }

  if (query.tab !== undefined && !isWalletTab(query.tab))
    router.replace({ path: route.path, query: { ...queryWithoutContext, tab: DEFAULT_WALLET_TAB } })
}, { immediate: true })

const tabList = computed(() => WALLET_TABS.map(tab => ({
  key: tab.key,
  label: t(tab.labelKey),
  active: tab.key === activeTab.value,
})))

function tabItemId(key: WalletTabKey) {
  return `tab-item-my-wallet-tabs-${key}`
}

function handleTabListUpdate(updatedTabList: { key: string, active?: boolean }[]) {
  const selectedTabKey = updatedTabList.find(tab => tab.active)?.key
  if (!isWalletTab(selectedTabKey) || selectedTabKey === activeTab.value)
    return

  router.push({ path: route.path, query: { ...route.query, tab: selectedTabKey } })
}
</script>

<template>
  <div v-if="isMyWalletEnabled" class="my-wallet-page">
    <h2 class="my-wallet-page-title">
      {{ t('my_wallet.title') }}
    </h2>

    <StatusBanner />

    <SolTabs
      id="my-wallet-tabs"
      :tab-list="tabList"
      :aria-label="t('my_wallet.tabs.aria')"
      @update:tab-list="handleTabListUpdate"
    >
      <template #tab:resumo>
        <div
          id="my-wallet-panel-resumo"
          class="my-wallet-panel"
          role="tabpanel"
          :aria-labelledby="tabItemId('resumo')"
        >
          <SummaryTab :receiving-model-tab="RECEIVING_MODEL_TAB" />
        </div>
      </template>
      <template #tab:modelo-de-recebimento>
        <div
          id="my-wallet-panel-modelo-de-recebimento"
          class="my-wallet-panel"
          role="tabpanel"
          :aria-labelledby="tabItemId('modelo-de-recebimento')"
        >
          <ReceivingModelTab />
        </div>
      </template>
    </SolTabs>
  </div>
</template>

<style scoped lang="scss">
.my-wallet-page {
  @apply flex flex-col gap-6 w-full min-h-full py-2xs px-3;
  @apply relative;

  @screen md:system {
    @apply px-2xs pb-sm pt-2xs;
  }

  .my-wallet-page-title {
    @apply fonts-heading-h2 text-neutral-low-pure;
  }

  .my-wallet-panel {
    @apply w-full mt-4 p-4 rounded-lg bg-neutral-high-pure;

    @screen sm:system {
      @apply p-6;
    }

    @screen md:system {
      @apply p-8;
    }
  }
}
</style>
